import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Typography, Button, TextField, Grid,
    MenuItem, CircularProgress, Chip, Card, CardContent,
    Avatar, Stack, FormControl, InputLabel,
    Select, Collapse, useMediaQuery, useTheme
} from '@mui/material';
import {
    Edit, PersonAdd, ExpandMore, ExpandLess
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const TeacherForm = ({
    open,
    onToggle,
    editingTeacher,
    onSuccess,
    onCancel,
    subjects = [],
    classes = []
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: '',
        email: '',
        gender: '',
        password: '',
        phone: '',
        subjects: [],
        assignedClasses: []
    });
    const [errors, setErrors] = useState({});

    // Populate form when editingTeacher changes
    useEffect(() => {
        if (editingTeacher) {
            const oldSubject = editingTeacher.subject ? [editingTeacher.subject._id] : [];
            const multiSubjects = Array.isArray(editingTeacher.subjects)
                ? editingTeacher.subjects.map(s => s._id)
                : oldSubject;

            setForm({
                name: editingTeacher.user?.name || '',
                email: editingTeacher.user?.email || '',
                gender: editingTeacher.user?.gender || editingTeacher.gender || '',
                password: '',
                phone: editingTeacher.phone || '',
                subjects: multiSubjects,
                assignedClasses: editingTeacher.assignedClasses?.map(c => c._id) || [],
                dateOfBirth: editingTeacher.dateOfBirth ? new Date(editingTeacher.dateOfBirth).toISOString().split('T')[0] : ''
            });
        } else {
            // Reset form if NOT editing (or if editingTeacher becomes null)
            setForm({
                name: '',
                email: '',
                gender: '',
                password: '',
                phone: '',
                subjects: [],
                assignedClasses: [],
                dateOfBirth: ''
            });
        }
    }, [editingTeacher]);

    const validateForm = () => {
        const newErrors = {
            name: !form.name,
            email: !form.email || !/\S+@\S+\.\S+/.test(form.email),
            gender: !form.gender,
            password: editingTeacher
                ? Boolean(form.password) && form.password.length < 6
                : (!form.password || form.password.length < 6),
            phone: !form.phone,
            subjects: !Array.isArray(form.subjects) || form.subjects.length === 0,
            assignedClasses: !Array.isArray(form.assignedClasses) || form.assignedClasses.length === 0
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: name === 'name' ? value.toUpperCase() : value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    }, []);

    const handleClassChange = useCallback((e) => {
        setForm(prev => ({ ...prev, assignedClasses: e.target.value }));
        setErrors(prev => ({ ...prev, assignedClasses: false }));
    }, []);

    const handleSubjectChange = useCallback((e) => {
        setForm(prev => ({ ...prev, subjects: e.target.value }));
        setErrors(prev => ({ ...prev, subjects: false }));
    }, []);

    const handleClear = () => {
        setForm({
            name: '',
            email: '',
            password: '',
            phone: '',
            subjects: [],
            assignedClasses: [],
            dateOfBirth: ''
        });
        setErrors({});
        if (onCancel) onCancel();
    };

    const handleSubmit = async () => {
        if (!validateForm()) return toast.error('Fix validation errors');

        setLoading(true);
        try {
            let res;
            if (editingTeacher) {
                const updateData = {
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    gender: form.gender,
                    subjects: form.subjects,
                    assignedClasses: form.assignedClasses,
                    dateOfBirth: form.dateOfBirth
                };
                if (form.password) updateData.password = form.password;

                res = await api.put(`/teachers/${editingTeacher._id}`, updateData);
            } else {
                res = await api.post('/teachers', {
                    ...form,
                    subjects: form.subjects
                });
            }

            if (res.data?.success) {
                toast.success(editingTeacher ? 'Teacher updated' : 'Teacher created');
                handleClear(); // Clear form and exit edit mode
                if (onSuccess) onSuccess();
            } else {
                throw new Error(res.data?.message || 'Unexpected error');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || err.message || 'Failed to save teacher');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{
            mb: 4,
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.divider}`
        }}>
            <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
                <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    spacing={isMobile ? 2 : 2}
                    sx={{ mb: 3 }}
                >
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: isMobile ? 40 : 48,
                            height: isMobile ? 40 : 48
                        }}
                    >
                        {editingTeacher ? <Edit fontSize={isMobile ? "small" : "medium"} /> : <PersonAdd fontSize={isMobile ? "small" : "medium"} />}
                    </Avatar>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        fontWeight="600"
                        sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
                    >
                        {editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}
                    </Typography>
                    <Button
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        startIcon={open ? <ExpandLess /> : <ExpandMore />}
                        onClick={onToggle}
                        sx={{ ml: isMobile ? 0 : 'auto', mt: isMobile ? 1 : 0 }}
                    >
                        {open ? 'Collapse' : 'Expand'}
                    </Button>
                </Stack>

                <Collapse in={open}>
                    <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Full Name"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                fullWidth
                                error={errors.name}
                                helperText={errors.name ? 'Name is required' : "Enter teacher's full name"}
                                disabled={loading}
                                placeholder="Enter teacher's full name"
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                                error={errors.email}
                                helperText={errors.email ? 'Valid email is required' : "Enter teacher's email address"}
                                disabled={loading}
                                placeholder="Enter teacher's email"
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label={editingTeacher ? "New Password" : "Password"}
                                name="password"
                                type="password"
                                value={form.password}
                                onChange={handleChange}
                                fullWidth
                                error={errors.password}
                                helperText={
                                    errors.password
                                        ? 'Minimum 6 characters required'
                                        : editingTeacher
                                            ? 'Leave blank to keep current password'
                                            : 'Set a password for the teacher'
                                }
                                disabled={loading}
                                placeholder={editingTeacher ? "Enter new password" : "Create a secure password"}
                                size={isMobile ? "small" : "medium"}
                                autoComplete="new-password"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth error={errors.gender} disabled={loading} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    name="gender"
                                    value={form.gender}
                                    onChange={handleChange}
                                    label="Gender"
                                >
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                </Select>
                                {errors.gender && <Typography variant="caption" color="error">Gender is required</Typography>}
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Date of Birth"
                                name="dateOfBirth"
                                type="date"
                                value={form.dateOfBirth}
                                onChange={handleChange}
                                fullWidth
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Phone Number"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                fullWidth
                                error={errors.phone}
                                helperText={errors.phone ? 'Phone number is required' : "Enter teacher's phone number"}
                                disabled={loading}
                                placeholder="Enter phone number"
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>

                        {/* Subjects (multi) */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth error={errors.subjects} disabled={loading || subjects.length === 0} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Subjects</InputLabel>
                                <Select
                                    multiple
                                    name="subjects"
                                    value={Array.isArray(form.subjects) ? form.subjects : []}
                                    onChange={handleSubjectChange}
                                    label="Subjects"
                                    renderValue={(selected) => (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {selected.map((id) => {
                                                const subj = subjects.find(s => s._id === id);
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={subj?.name || 'Unknown'}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    )}
                                >
                                    {useMemo(() => subjects.map(subj => (
                                        <MenuItem key={subj._id} value={subj._id}>
                                            {subj.name}
                                        </MenuItem>
                                    )), [subjects])}
                                </Select>

                                {errors.subjects ? (
                                    <Typography variant="caption" color="error">
                                        Select at least one subject
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        Select one or more subjects for this teacher
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Assigned Classes (multi) */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth error={errors.assignedClasses} disabled={loading || classes.length === 0} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Assigned Classes</InputLabel>
                                <Select
                                    multiple
                                    value={Array.isArray(form.assignedClasses) ? form.assignedClasses : []}
                                    onChange={handleClassChange}
                                    label="Assigned Classes"
                                    renderValue={(selected) => (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {selected.map((id) => {
                                                const cls = classes.find(c => c._id === id);
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={cls?.displayName || cls?.name || 'Unknown'}
                                                        size="small"
                                                        color="secondary"
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    )}
                                >
                                    {useMemo(() => classes.map(cls => (
                                        <MenuItem key={cls._id} value={cls._id}>
                                            {cls.displayName || cls.name}
                                        </MenuItem>
                                    )), [classes])}
                                </Select>
                                {errors.assignedClasses ? (
                                    <Typography variant="caption" color="error">
                                        At least one class must be assigned
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        Select classes this teacher will teach
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Stack
                                direction={isMobile ? "column" : "row"}
                                spacing={2}
                                justifyContent="flex-end"
                                sx={{ gap: isMobile ? 1 : 2 }}
                            >
                                {editingTeacher && (
                                    <Button
                                        onClick={handleClear}
                                        variant="outlined"
                                        disabled={loading}
                                        fullWidth={isMobile}
                                        size={isMobile ? "medium" : "large"}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    onClick={handleSubmit}
                                    variant="contained"
                                    size={isMobile ? "medium" : "large"}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : null}
                                    fullWidth={isMobile}
                                >
                                    {loading ? 'Saving...' : editingTeacher ? 'Update Teacher' : 'Create Teacher'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default TeacherForm;
